import React, { useState, useEffect } from 'react';
import { Card } from "@/components/ui/card";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/components/auth/AuthContext";
import {
  Table,
  TableHeader,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@/components/ui/table";

export function ProductosABC() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productosData, setProductosData] = useState([]);
  
  // Establecer fechas por defecto (último mes)
  const today = new Date();
  const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
  const [fechaDesde, setFechaDesde] = useState(lastMonth.toISOString().split('T')[0]);
  const [fechaHasta, setFechaHasta] = useState(today.toISOString().split('T')[0]);

  useEffect(() => {
    if (fechaDesde && fechaHasta) {
      cargarDatosProductos();
    }
  }, [fechaDesde, fechaHasta]);

  const cargarDatosProductos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let query = supabase
        .from('orders')
        .select(`
          id,
          created_at,
          cod_rep,
          items:order_items(
            id,
            quantity,
            price,
            product:products(
              id,
              code,
              description
            )
          )
        `)
        .gte('created_at', fechaDesde)
        .lte('created_at', fechaHasta);

      // Si el usuario no tiene permiso para ver comisiones globales, filtrar por su cod_rep
      if (!user.ver_comisiones_globales) {
        query = query.eq('cod_rep', user.cod_rep);
      }

      const { data: pedidosData, error: pedidosError } = await query;

      if (pedidosError) throw pedidosError;

      // Procesar datos para el análisis ABC
      const productosVentas = {};
      
      pedidosData?.forEach(pedido => {
        pedido.items?.forEach(item => {
          if (!item.product) return;
          
          const productoId = item.product.id;
          
          if (!productosVentas[productoId]) {
            productosVentas[productoId] = {
              id: productoId,
              nombre: item.product.description,
              referencia: item.product.code,
              cantidad: 0,
              valor: 0
            };
          }
          
          productosVentas[productoId].cantidad += item.quantity;
          productosVentas[productoId].valor += item.quantity * item.price;
        });
      });

      // Convertir a array y ordenar por cantidad
      let productosArray = Object.values(productosVentas);
      productosArray.sort((a, b) => b.cantidad - a.cantidad);

      // Calcular porcentajes individuales y acumulados basados en cantidad
      const cantidadTotal = productosArray.reduce((sum, p) => sum + p.cantidad, 0);
      let acumulado = 0;

      productosArray = productosArray.map((producto, index) => {
        // Calcular porcentaje individual que representa este producto del total
        const porcentajeIndividual = (producto.cantidad / cantidadTotal) * 100;
        
        // Calcular porcentaje acumulado para la categorización ABC
        acumulado += producto.cantidad;
        const porcentajeAcumulado = (acumulado / cantidadTotal) * 100;
        
        let categoria = 'C';
        if (porcentajeAcumulado <= 80) categoria = 'A';
        else if (porcentajeAcumulado <= 95) categoria = 'B';

        return {
          ...producto,
          porcentajeIndividual,
          porcentajeAcumulado,
          categoria,
          valorFormateado: new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR'
          }).format(producto.valor)
        };
      });

      setProductosData(productosArray);
    } catch (error) {
      console.error('Error al cargar datos de productos:', error);
      setError('Error al cargar datos de productos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="text-xl font-bold mb-4">Análisis ABC de Productos</h2>
      <div className="flex gap-4 items-end mb-4">
        <div>
          <label className="block text-sm font-medium mb-1">Desde</label>
          <input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            className="border rounded p-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Hasta</label>
          <input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            className="border rounded p-2"
          />
        </div>
      </div>

      {error && (
        <div className="text-red-500 mb-4">{error}</div>
      )}

      {loading ? (
        <div>Cargando datos...</div>
      ) : productosData.length === 0 ? (
        <div className="text-gray-500">No hay datos de productos para el período seleccionado</div>
      ) : (
        <>
          <div className="mb-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Producto</TableHead>
                  <TableHead>Referencia</TableHead>
                  <TableHead className="text-right">Cantidad</TableHead>
                  {user.ver_comisiones_globales && (
                    <TableHead className="text-right">Valor Total</TableHead>
                  )}
                  <TableHead className="text-right">% del Total</TableHead>
                  <TableHead>Categoría</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {productosData.map((producto, index) => (
                  <TableRow key={index} className={
                    producto.categoria === 'A' ? 'bg-blue-50' :
                    producto.categoria === 'B' ? 'bg-green-50' :
                    'bg-gray-50'
                  }>
                    <TableCell>{producto.nombre}</TableCell>
                    <TableCell>{producto.referencia}</TableCell>
                    <TableCell className="text-right">{producto.cantidad.toLocaleString('es-ES')}</TableCell>
                    {user.ver_comisiones_globales && (
                      <TableCell className="text-right">{producto.valorFormateado}</TableCell>
                    )}
                    <TableCell className="text-right">{producto.porcentajeIndividual.toFixed(2)}%</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                        producto.categoria === 'A' ? 'bg-blue-100 text-blue-800' :
                        producto.categoria === 'B' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {producto.categoria}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-4">
            <div className="bg-blue-100 p-4 rounded">
              <h3 className="font-bold text-blue-800">Categoría A</h3>
              <p className="text-sm text-blue-600">80% de las unidades vendidas</p>
            </div>
            <div className="bg-green-100 p-4 rounded">
              <h3 className="font-bold text-green-800">Categoría B</h3>
              <p className="text-sm text-green-600">15% de las unidades vendidas</p>
            </div>
            <div className="bg-gray-100 p-4 rounded">
              <h3 className="font-bold text-gray-800">Categoría C</h3>
              <p className="text-sm text-gray-600">5% de las unidades vendidas</p>
            </div>
          </div>
        </>
      )}
    </Card>
  );
} 